import { Typography, Stack } from '@mui/material';
import Fuse from 'fuse.js';
import { FC, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Counter } from '../../components/Counter';
import LoadingSpinner from '../../components/LoadingSpinner';
import { SearchField } from '../../components/SearchField';
import { numberAsKr } from '../../Formatters';
import { RenewalInfo } from '../products';
import { useRenewalContext } from '../RenewalContext';
import { useCurrents } from '../useData';
import moment from 'moment';
import { DiffMaconomyApi } from '../Diff/DiffMaconomyApiDialog';
import { DiffMaconomyFileDialog } from '../Diff/DiffMaconomyFileDialog';
import { ExcelExport } from './ExcelExport';
import { useRenewalProducts } from '../products';
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro';
import { nbNO } from '@mui/x-data-grid/locales';

export const RenewalTable: FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const { renewalDate } = useRenewalContext();
  const { renewals, loading } = useCurrents();

  const filteredRenewals = renewals ? filterRenewals(renewals, searchTerm) : [];
  const notRenewedYet = renewalDate > moment();
  const products = useRenewalProducts();

  const columns = useMemo<GridColDef<RenewalInfo>[]>(
    () => [
      {
        field: 'name',
        headerName: 'Kunde',
        minWidth: 120,
        flex: 1,
        renderCell: (params) => <Link to={'/license/' + params.row.licenseId}>{params.row.name}</Link>,
      },
      {
        field: 'subscriptionOrderNumber',
        headerName: 'Abonnement',
        minWidth: 120,
        flex: 1,
      },
      ...(products ?? []).map(
        (p): GridColDef<RenewalInfo> => ({
          field: p.number,
          headerName: p.name,
          minWidth: 120,
          flex: 1,
          type: 'number',
          align: 'left',
          headerAlign: 'left',
          sortable: true,
          valueGetter: (_, row) => p.selector(row) ?? 0,
          renderCell: (params) => <span>{numberAsKr(p.selector(params.row))}</span>,
        }),
      ),
      {
        field: 'total',
        headerName: 'Sum',
        minWidth: 120,
        flex: 1,
        type: 'number',
        align: 'left',
        headerAlign: 'left',
        renderCell: (params) => <span>{numberAsKr(params.row.total)}</span>,
      },
    ],
    [products],
  );

  return (
    <div>
      <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
        <Typography variant="h6">Kunder i fornyelse</Typography>
        <Stack direction="row">
          {notRenewedYet && <DiffMaconomyApi />}
          <DiffMaconomyFileDialog />
          {renewals && products && <ExcelExport current={renewals} products={products} name="Kunder i fornyelse" />}
        </Stack>
      </Stack>
      <SearchField filter={setSearchTerm} />
      <Counter list={filteredRenewals} />
      {loading && <LoadingSpinner />}
      {renewals && (
        <DataGridPro
          autoHeight
          density="compact"
          rows={filteredRenewals}
          columns={columns}
          getRowId={(row) => row.id}
          localeText={nbNO.components.MuiDataGrid.defaultProps.localeText}
          pagination
          pageSizeOptions={[50, 100, 250]}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 50,
                page: 0,
              },
            },
            pinnedColumns: {
              right: ['total'],
            },
          }}
          sx={{
            mt: 1,
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 700,
            },
          }}
        />
      )}
    </div>
  );
};

function filterRenewals(renewals: RenewalInfo[], searchTerm: string) {
  var options = {
    keys: ['name', 'subscriptionOrderNumber'],
  };
  if (!searchTerm) {
    return renewals;
  } else {
    var fuse = new Fuse(renewals, options);
    var filtered = fuse.search(searchTerm).map((r: any) => r.item);
    return filtered;
  }
}
